/**
 * Branding per-tenant con **eredità dal master**.
 *
 * I campi (`primaryColor`, `secondaryColor`, `uiThemeJson`, logo) vivono già sul tenant: quello che
 * mancava non era il dato, era la regola su cosa succede quando un tenant **non** li ha valorizzati.
 * Prima ricadevano sul default MaeDesign compilato nel bundle; ora ricadono sul **master**, che è ciò
 * che una holding si aspetta — le società nuove nascono già vestite come il gruppo, e chi vuole
 * distinguersi lo dichiara.
 *
 * **Due blocchi, non tredici campi.** L'eredità si decide su *colori* e *layout* interi, non campo per
 * campo. Un merge a livello di singolo campo produrrebbe combinazioni che nessuno ha progettato
 * (header del master sopra la sidebar del tenant), e soprattutto renderebbe «Ripristina eredità»
 * ambiguo: ripristinare *cosa*? Con due blocchi la domanda ha una risposta sola.
 *
 * ⚠️ **Il valore risolto non si salva.** L'editor mostra i valori ereditati come punto di partenza,
 * ma finché il blocco è ereditato i campi restano `null` sul tenant. Se li materializzassimo al primo
 * salvataggio, ogni tenant diventerebbe silenziosamente «personalizzato» e le modifiche successive
 * del master non lo raggiungerebbero più — cioè l'eredità si spegnerebbe da sola senza che nessuno
 * l'abbia chiesto.
 */
import { parseUiTheme, type UiTheme } from '@/shared/lib/uiTheme';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';

/**
 * Il tenant di sistema, da cui tutti gli altri ereditano.
 *
 * ⚠️ Costante e non un dato letto dal BE perché **`isSystemTenant` è `false` su tutti i tenant** e il
 * filtro `equals(isSystemTenant,'true')` torna zero righe (verificato sul dev). È lo stesso GUID che
 * il BE usa come `MasterTenant` nei suoi test. Quando il flag verrà valorizzato, questa costante
 * diventa un fallback e la ricerca passa da lì — vedi `masterTenantIdFrom`.
 */
export const MASTER_TENANT_ID = '00000000-0000-0000-0000-000000000001';

/** La forma minima di tenant che serve qui: ogni chiamante ne ha una più ricca. */
export interface BrandingLike {
  id: string;
  name?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  uiThemeJson?: string | null;
  isSystemTenant?: boolean | null;
}

/**
 * Il master fra i tenant noti: preferisce il flag `isSystemTenant`, ricade sulla costante.
 * Così il giorno in cui il BE valorizza il flag questa funzione inizia a usarlo senza altri interventi.
 */
export function masterTenantIdFrom(tenants: BrandingLike[]): string {
  return tenants.find((t) => t.isSystemTenant === true)?.id ?? MASTER_TENANT_ID;
}

export function isMasterTenant(tenantId: string | null | undefined, tenants: BrandingLike[] = []): boolean {
  return !!tenantId && tenantId === masterTenantIdFrom(tenants);
}

/** Da dove arriva ciò che si vede. */
export type BrandingSource = 'own' | 'inherited' | 'default';

export const SOURCE_LABEL: Record<BrandingSource, string> = {
  own: 'Personalizzato',
  inherited: 'Ereditato dal master',
  default: 'Default MaeDesign',
};

/** Un colore vale come «impostato» solo se è un hex leggibile: `''` e spazi non sono una scelta. */
const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
function validHex(v?: string | null): string | null {
  const t = v?.trim();
  return t && HEX_RE.test(t) ? t : null;
}

/** Il tenant ha colori suoi? Basta uno dei due: il blocco è indivisibile. */
export function hasOwnColors(t?: BrandingLike | null): boolean {
  return !!(validHex(t?.primaryColor) || validHex(t?.secondaryColor));
}

/** Il tenant ha un layout suo? `uiThemeJson` vuoto o illeggibile conta come «no». */
export function hasOwnLayout(t?: BrandingLike | null): boolean {
  const raw = t?.uiThemeJson?.trim();
  if (!raw) return false;
  const parsed = parseUiTheme(raw);
  return !!parsed && Object.keys(parsed).length > 0;
}

export interface ResolvedBranding {
  primaryColor: string | null;
  secondaryColor: string | null;
  uiTheme: UiTheme | null;
  /** Provenienza del blocco colori e di quello layout, per dirlo in interfaccia. */
  colorsFrom: BrandingSource;
  layoutFrom: BrandingSource;
}

/**
 * I valori da **applicare**: propri se il tenant li ha, del master altrimenti, `null` se nessuno dei
 * due li ha (e allora vale il default MaeDesign del bundle, che resta l'ultima rete).
 *
 * `master` può essere `null` — quando è il master stesso a essere il tenant corrente, o quando non lo
 * si è ancora letto. In quel caso non si eredita niente e si ricade sul default: mai su valori
 * parziali, che darebbero un tema a metà durante il caricamento.
 */
export function resolveBranding(
  own?: BrandingLike | null,
  master?: BrandingLike | null,
): ResolvedBranding {
  const ownColors = hasOwnColors(own);
  const masterColors = hasOwnColors(master);
  const colorsFrom: BrandingSource = ownColors ? 'own' : masterColors ? 'inherited' : 'default';
  const colorSrc = ownColors ? own : masterColors ? master : null;

  const ownLayout = hasOwnLayout(own);
  const masterLayout = hasOwnLayout(master);
  const layoutFrom: BrandingSource = ownLayout ? 'own' : masterLayout ? 'inherited' : 'default';
  const layoutSrc = ownLayout ? own : masterLayout ? master : null;

  return {
    primaryColor: validHex(colorSrc?.primaryColor),
    secondaryColor: validHex(colorSrc?.secondaryColor),
    uiTheme: layoutSrc ? parseUiTheme(layoutSrc.uiThemeJson) : null,
    colorsFrom,
    layoutFrom,
  };
}

/**
 * Gli attributi da mandare in PATCH per **riportare un blocco all'eredità**.
 *
 * Azzerare è l'unico modo per tornare ad ascoltare il master: finché un valore c'è, vince sul suo.
 * Sono `null` espliciti e non campi omessi — omettere significa «non toccare», che è l'opposto.
 */
export function clearColorsPatch(): Record<string, unknown> {
  return { primaryColor: null, secondaryColor: null };
}

export function clearLayoutPatch(): Record<string, unknown> {
  return { uiThemeJson: null };
}

/**
 * Riepilogo in una riga per l'elenco dei tenant: dice a colpo d'occhio chi si è vestito da sé.
 * Serve a rendere visibile, prima di aprire una scheda, che quel tenant non seguirà più il master.
 */
export function brandingSummary(t: BrandingLike): string {
  const parts: string[] = [];
  if (hasOwnColors(t)) parts.push('colori propri');
  if (hasOwnLayout(t)) parts.push('layout proprio');
  return parts.length ? parts.join(' · ') : 'Tutto ereditato dal master';
}

/* ───────────────────── il master fuori perimetro (BE 19 ago 2026) ───────────────────── */

/**
 * `true` se la lettura del master è fallita perché quell'azienda è **fuori dal perimetro** di chi
 * guarda, non per un guasto.
 *
 * Da `TENANT-VISIBILITY-SCOPE` (19 ago) `GET /api/tenants/{id}` risponde **404** per un'azienda che
 * non è né la propria, né una di cui si è membri, né una discendente (col claim), né del proprio
 * gruppo (col claim) — con l'eccezione dichiarata della **catena degli antenati**. Il BE ha scelto
 * 404 e non 403 perché «un 403 confermerebbe che quell'azienda esiste, che è già un'informazione».
 *
 * Perché ci riguarda: l'eredità del branding legge il **master per id fisso**. Per i tenant che
 * stanno sotto il master è un antenato e resta leggibile; per una **radice indipendente** non lo è,
 * e quella lettura ora è un 404 legittimo. Distinguerlo da un guasto cambia cosa si dice
 * all'utente: «riprova» su un 404 di perimetro è un bottone che non funzionerà mai.
 */
export function masterOutOfScope(err: unknown): boolean {
  return err instanceof JsonApiHttpError && err.status === 404;
}
